/**
 * Pending step-up resumes (ALL-5021 / ALL-5022): what a mutating call needs in
 * order to re-issue itself after the browser has been sent through a step-up
 * re-authentication (`/auth/login?step_up_challenge_id=<id>`).
 *
 * Why the challenge id *and* its raw nonce are persisted at all. The gateway's
 * AAL2 step-up is created and completed against the browser session, and a
 * session that is too old, or is not MFA-backed, can never satisfy it however
 * often the challenge is completed: AuthStepUpService#completeChallenge runs
 * requireAssurance() after the nonce check and answers
 * AUTH_ASSURANCE_INSUFFICIENT / AUTH_STEP_UP_EXPIRED for exactly that session.
 * The only way up is a fresh IdP round trip, and /auth/login's
 * `step_up_challenge_id` parameter exists for it (ALL-5021): the gateway
 * requires the caller to already hold a browser session, and
 * BrowserAuthSessionService#startLogin refuses a challenge id that is not still
 * ISSUED, is expired, or is not owned by the calling session's principal. So the
 * intended usage is that ONE challenge survives the redirect and is completed
 * afterwards — minting a new challenge after the return would re-authenticate
 * against a challenge the login transaction never recorded, and the completed
 * nonce is single-use (a second completion fails requireIssued with
 * AUTH_STEP_UP_INVALID). `GatewayBrowserOAuthControllerTest` /
 * `AuthStepUpServiceTest` are the backend proof of that ordering.
 *
 * What is never stored here: session cookies, tokens, JWTs, CSRF values or any
 * other credential. The nonce is a single-use challenge value that proves
 * nothing on its own — the auth service independently requires the browser
 * session to own the challenge and to carry the required assurance — and the
 * session cookie stays HttpOnly and is never readable from JS.
 */
export const STEP_UP_RESUME_STORAGE_KEY = "alloy.sandbox.step-up-resume.v1";

/** Cap for the persisted return path, matching the gateway's own bound. */
const MAX_RETURN_PATH = 240;
const MAX_FIELD = 512;

/**
 * Upper bound on how long a pending resume may sit in sessionStorage. A record
 * expires at the EARLIER of the challenge's own `expiresAt` (AuthStepUpService
 * challenge TTL, which stays authoritative: the server stops accepting the nonce
 * whenever it decides) and now + this bound, and the bound alone applies when the
 * challenge response carried no `expiresAt`. It only limits how long a parked
 * write can wait; it can never extend a challenge or skip step-up. Typed build-time config with a validated default:
 * `VITE_STEP_UP_RESUME_TTL_MINUTES`, clamped to 1..60 minutes, same
 * `import.meta.env` pattern as lib/feature-flags.ts.
 */
export const STEP_UP_RESUME_TTL_DEFAULT_MINUTES = 15;

export function parseStepUpResumeTtlMinutes(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.min(Math.max(Math.round(parsed), 1), 60);
}

export function resolveStepUpResumeTtlMinutes(raw: string | undefined): number {
  return parseStepUpResumeTtlMinutes(raw) ?? STEP_UP_RESUME_TTL_DEFAULT_MINUTES;
}

function configuredTtlMinutes(): number {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return resolveStepUpResumeTtlMinutes(env?.VITE_STEP_UP_RESUME_TTL_MINUTES);
}

/** The fallback TTL in milliseconds, read once at module load. */
export const STEP_UP_RESUME_FALLBACK_TTL_MS = configuredTtlMinutes() * 60_000;

export interface PendingStepUpResume {
  /** Operation descriptor; must be a key of STEP_UP_RESUME_ACTIONS in lib/api. */
  action: string;
  /** Exactly the arguments re-issuing that operation needs, verbatim. */
  payload: unknown;
  /** The `Idempotency-Key` both the first attempt and the replay send, so the
   * gateway sees one write rather than two. */
  idempotencyKey: string;
  /** The challenge the session must complete after re-authenticating. */
  challengeId: string;
  /** That challenge's raw nonce (single-use). */
  nonce: string;
  /** Same-origin path the IdP round trip returns to. */
  returnPath: string;
  /** When the challenge itself expires (AuthStepUpChallengeResult.expiresAt). */
  expiresAt: string;
}

export type PendingStepUpResumeLookup =
  | { status: "none" }
  | { status: "expired" }
  | { status: "pending"; record: PendingStepUpResume };

function storage(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    // Blocked storage (private mode, hardened settings) must disable the resume,
    // never the action: callers treat "no storage" as "no resume available".
    return null;
  }
}

/**
 * The gateway sanitises `return_to` with the same rules before it will redirect
 * back (`GatewayBrowserOAuthController#sanitizeReturnPath`): an absolute
 * same-origin path, never protocol-relative, never carrying a scheme, never with a
 * backslash. This check is stricter than the gateway's: it also refuses every
 * ASCII control character (a browser strips TAB/CR/LF inside a URL, so
 * "/\t/evil.example" would otherwise become the protocol-relative
 * "//evil.example"), and, where a browser origin exists, it resolves the value
 * against that origin and requires the result to stay on it. A record whose
 * return path fails this is discarded rather than followed.
 */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;

export function isSafeReturnPath(value: unknown): value is string {
  if (
    typeof value !== "string"
    || !value.startsWith("/")
    || value.startsWith("//")
    || value.includes("\\")
    || value.includes("://")
    || CONTROL_CHARACTERS.test(value)
    || value.length > MAX_RETURN_PATH
  ) {
    return false;
  }
  const origin = typeof window !== "undefined" ? window.location?.origin : undefined;
  if (!origin || origin === "null") return true;
  try {
    return new URL(value, origin).origin === origin;
  } catch {
    return false;
  }
}

/** The path part of a return path (no query, no fragment). */
export function returnPathname(returnPath: string): string {
  const cut = returnPath.search(/[?#]/);
  return cut === -1 ? returnPath : returnPath.slice(0, cut);
}

function boundedString(value: unknown, max = MAX_FIELD): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed && trimmed.length <= max ? trimmed : null;
}

/** Re-validates everything read back from storage: sessionStorage is writable by
 * any same-origin script and by an older build of this app, so a record is only
 * trusted once it parses *and* type-checks. */
function validRecord(value: unknown): PendingStepUpResume | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const action = boundedString(raw.action, 64);
  const idempotencyKey = boundedString(raw.idempotencyKey, 128);
  const challengeId = boundedString(raw.challengeId, 128);
  const nonce = boundedString(raw.nonce, 256);
  const expiresAt = boundedString(raw.expiresAt, 64);
  if (!action || !idempotencyKey || !challengeId || !nonce || !expiresAt) return null;
  if (!isSafeReturnPath(raw.returnPath)) return null;
  if (!Number.isFinite(Date.parse(expiresAt))) return null;
  return { action, idempotencyKey, challengeId, nonce, returnPath: raw.returnPath, expiresAt, payload: raw.payload };
}

function readRecord(store: Storage): PendingStepUpResume | null {
  let raw: string | null;
  try {
    raw = store.getItem(STEP_UP_RESUME_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearPendingStepUpResume();
    return null;
  }
  const record = validRecord(parsed);
  if (!record) {
    // Unusable: it can never be resumed, so it must not sit there blocking the
    // next action's record either.
    clearPendingStepUpResume();
    return null;
  }
  return record;
}

export function clearPendingStepUpResume(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(STEP_UP_RESUME_STORAGE_KEY);
  } catch {
    // Nothing to do: an unremovable record fails its own expiry check anyway.
  }
}

/** Persists one pending resume. One at a time by design: a second
 * step-up-required write in the same tab replaces the first, so a stale
 * half-finished action can never be replayed behind the user's back. */
export function savePendingStepUpResume(record: PendingStepUpResume): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(STEP_UP_RESUME_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage full or blocked: the caller still redirects, and the action simply
    // is not resumed afterwards.
  }
}

export function isPendingStepUpResumeExpired(record: PendingStepUpResume, now = Date.now()): boolean {
  const expiresAt = Date.parse(record.expiresAt);
  return !Number.isFinite(expiresAt) || expiresAt <= now;
}

/**
 * Consumes the pending record when it belongs to one of `actions`.
 *
 * A record for another action is left completely alone — untouched and
 * unreported — because each surface resumes only its own write: two hooks can
 * live on one page (user detail has one for suspend, one for the role change), and
 * on the page that owns a record its hook must be the one that acts on it. That
 * includes an expired record: the surface that owns it is the only surface that
 * can tell the person their write was not applied, so a non-owning hook that
 * happened to mount first (and consumed it, in an earlier revision) would leave
 * them with no explanation at all.
 *
 * An expired record of our own is discarded rather than replayed — the challenge
 * behind it can no longer be completed (AuthStepUpService#requireCurrent marks it
 * EXPIRED), so replaying its nonce could only fail. The record is removed *before*
 * the challenge is completed and the request replayed: the nonce is single-use, and
 * this is what makes "re-issues the request exactly once" hold even if the page
 * reloads mid-resume.
 */
export function takePendingStepUpResume(
  actions: readonly string[],
  now = Date.now(),
  currentPathname?: string,
): PendingStepUpResumeLookup {
  const store = storage();
  if (!store) return { status: "none" };
  const record = readRecord(store);
  if (!record) return { status: "none" };
  if (!actions.includes(record.action)) return { status: "none" };
  // Only the page the record names may act on it: the suspend hook on user B's
  // page must never replay a suspend parked for user A. A record for another
  // page is left untouched and simply expires with its challenge.
  if (currentPathname !== undefined && returnPathname(record.returnPath) !== currentPathname) {
    return { status: "none" };
  }
  clearPendingStepUpResume();
  if (isPendingStepUpResumeExpired(record, now)) return { status: "expired" };
  return { status: "pending", record };
}

/**
 * Navigates the browser to the step-up re-authentication URL. A full navigation,
 * not a router push: the gateway answers /auth/login with a 302 to the identity
 * provider, which only a real navigation follows (same as LoginPage#startLogin).
 */
export function redirectToStepUpLogin(path: string): void {
  window.location.assign(path);
}
